import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import BlogPostLayout from '../../components/redesign/BlogPostLayout.jsx';
import setSeo from '../../utils/seo.js';
import heroImage from '../../assets/llm-wiki-hero.png';

export default function LLMWikiCRMBlog() {
  useEffect(() => {
    const canonicalUrl = `${window.location.origin}/blog/llm-wiki-crm`;
    setSeo({
      title: 'Your CRM Is a Folder of Markdown Files | Aakeeo',
      description:
        'What if your CRM was just markdown files maintained by an LLM? No forms, no fields, no data entry. We built it, tested it, and here is what actually happened.',
      keywords: ['LLM Wiki CRM', 'markdown CRM', 'Obsidian CRM', 'AI CRM', 'Salesforce alternative'],
      canonical: canonicalUrl,
      og: {
        title: 'Your CRM Is a Folder of Markdown Files',
        description: 'No forms, no fields, no data entry. An LLM maintains your entire sales pipeline from meeting notes.',
        url: canonicalUrl,
        image: window.location.origin + heroImage,
        type: 'article',
      },
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: "Your CRM Is a Folder of Markdown Files — Here's How It Actually Works",
        url: canonicalUrl,
        mainEntityOfPage: canonicalUrl,
        datePublished: '2026-04-10',
        dateModified: '2026-04-10',
        wordCount: 3200,
        author: {
          '@type': 'Person',
          name: 'Arun Chauhan',
          jobTitle: 'Founder & CRM Consultant',
          worksFor: { '@type': 'Organization', name: 'Aakeeo Technologies' },
        },
        publisher: {
          '@type': 'Organization',
          name: 'Aakeeo Technologies',
          logo: { '@type': 'ImageObject', url: 'https://www.aakeeo.com/Aakeeo_favicon.png' },
        },
      },
    });
  }, []);

  return (
    <BlogPostLayout
      title="Your CRM is a folder of markdown files — here's how it actually works."
      category="AI & Automation"
      date="Apr 10, 2026"
      readTime="12 min"
      image={heroImage}
      excerpt="What if your CRM was just markdown files maintained by an LLM? No forms, no fields, no data entry. We built it, tested it, and here's what happened."
    >
      <p>
        <strong>I&apos;ve been a CRM consultant for 15 years.</strong> In that time, I&apos;ve watched more CRM implementations fail than succeed. And the reason is almost never the technology. It&apos;s that nobody uses the thing.
      </p>
      <p>
        After a sales call, the last thing anyone wants to do is update five different fields in Salesforce. So they don&apos;t. Data goes stale. Pipelines become fiction. The CRM slowly dies — not from a scaling problem, but from an adoption problem.
      </p>
      <p>Then I came across Andrej Karpathy&apos;s &ldquo;LLM Wiki&rdquo; gist, and it changed how I think about this entirely.</p>
      <p>
        What if the CRM is literally just a bunch of markdown files? And an LLM sits on top, takes whatever you write in natural language, and turns it into structure — linking contacts to companies, deals to interactions, tasks to follow-ups — automatically?
      </p>
      <p>
        I built it. <a href="https://github.com/Aakeeo/llm-wiki-crm" target="_blank" rel="noopener noreferrer">It&apos;s open source</a>. And it&apos;s weirdly usable.
      </p>

      <div className="callout">
        <p><strong>Want the full system?</strong> The LLM Wiki CRM ships with templates, dashboards, and a complete instruction set for Claude, Cursor, or any LLM agent. <Link to="/resources/llm-wiki-crm">Get it here →</Link></p>
      </div>

      <h2>The problem with every CRM you&apos;ve used</h2>
      <p>We don&apos;t think in fields. We think in sentences. &ldquo;Spoke to Rahul. Interested in WhatsApp automation. Follow up next Tuesday.&rdquo;</p>
      <p>That&apos;s everything you need. But CRMs force you to break that into pieces — open the contact record, update the &ldquo;last contacted&rdquo; field, change the deal stage dropdown, create a task with a due date, add an activity note. Five clicks minimum, across three screens.</p>
      <p>The friction isn&apos;t a UX problem. It&apos;s a fundamental mismatch between how humans communicate and how databases store information.</p>
      <p>When LLMs got good enough, my first instinct was the same as everyone else&apos;s — RAG pipelines, graph databases, natural-language querying on top of existing CRM schemas. I even looked at Neo4j.</p>
      <p>But then I saw Karpathy&apos;s LLM Wiki idea, and I paused.</p>
      <blockquote>What if we&apos;re overcomplicating this? What if the storage layer itself is just text files, and the LLM is the intelligence layer that creates and maintains the structure?</blockquote>

      <h2>How the LLM Wiki CRM actually works</h2>
      <p>The architecture is almost aggressively simple:</p>
      <pre><code>{`├── CLAUDE.md              # LLM instruction set (the "brain")
├── _templates/            # Entity templates with YAML frontmatter
├── _inbox/                # Drop raw notes here
├── _dashboards/           # Dataview queries for reporting
├── contacts/              # One markdown file per contact
├── companies/             # One markdown file per company
├── deals/                 # One markdown file per deal
├── interactions/          # Meeting/call/email records
├── tasks/                 # Follow-up action items
└── wiki/                  # Index and activity log`}</code></pre>
      <p>
        Every entity is a markdown file with YAML frontmatter (queryable metadata) and <code>[[wikilinks]]</code> for cross-referencing. Contacts link to companies. Deals link to contacts. Interactions link to everything.
      </p>

      <h3>The four workflows</h3>
      <p><strong>1. Ingest</strong> — Drop a meeting note into <code>_inbox/</code>. Tell the LLM to process it. It extracts contacts, companies, deals, interactions and tasks — creating or updating files automatically. A single meeting note typically generates 5–10 linked pages.</p>
      <p><strong>2. Query</strong> — Ask &ldquo;What&apos;s our pipeline value?&rdquo; or &ldquo;Who are our warmest leads?&rdquo; The LLM synthesises answers across all your markdown files with citations.</p>
      <p><strong>3. Update</strong> — Say &ldquo;Move the Acme deal to negotiation&rdquo; or &ldquo;Mark Jane as inactive.&rdquo; The LLM finds the right file, updates the frontmatter, and logs the change.</p>
      <p><strong>4. Lint</strong> — Request a health check. The system scans for stale deals, missing data, broken links, overdue tasks, and orphan pages — generates a report with suggested fixes.</p>

      <h2>What surprised me: I actually keep it updated</h2>
      <p><strong>I actually use this CRM.</strong> I&apos;m not &ldquo;doing data entry.&rdquo; I&apos;m just writing things down. The same notes I&apos;d write anyway after a call. And because everything is linked, context builds itself. Things start connecting on their own instead of being forced into fields.</p>
      <p>At some point it stops feeling like a CRM and more like a system that just… understands what&apos;s going on. For managing 30–50 clients as a solo operator or small team, it genuinely works.</p>

      <h2>The objections (I&apos;ve heard them all)</h2>

      <h3>&ldquo;LLMs aren&apos;t deterministic. This is a compliance nightmare.&rdquo;</h3>
      <p>Fair. An LLM processing the same input twice might produce slightly different output. For a regulated financial services firm, that&apos;s a non-starter.</p>
      <p>But most CRM failures I&apos;ve seen weren&apos;t compliance problems. They were <em>nobody-enters-data</em> problems. If you&apos;re choosing between a perfectly structured CRM with stale data and a markdown CRM with current data — which one is actually more useful?</p>

      <h3>&ldquo;This doesn&apos;t scale. What about hundreds of files?&rdquo;</h3>
      <p>Context-window question. For the size where most CRMs actually fail (5–50 users, a few hundred contacts), you&apos;re nowhere near the limit. The architecture is also designed to evolve — you can add a RAG retrieval layer or a structured backend underneath without changing the input experience.</p>

      <h3>&ldquo;Two people editing the same deal record = chaos.&rdquo;</h3>
      <p>This is the real limit. The moment you need multi-user access with permissions, audit trails and conflict resolution, you&apos;re building an application whether you planned to or not.</p>
      <p>
        I&apos;m not pretending this replaces <Link to="/services/suitecrm-implementation">a properly implemented SuiteCRM</Link> for a 50-person sales team. What it does is serve the huge gap below that — solo operators, founders, small teams — who need pipeline visibility but won&apos;t maintain a traditional CRM.
      </p>

      <h3>&ldquo;You&apos;ll end up rebuilding a CRM anyway.&rdquo;</h3>
      <p>Probably. But the question isn&apos;t whether you&apos;ll eventually need structure. It&apos;s: <strong>can you get a really strong capture layer before you hit that complexity wall?</strong></p>
      <p>Once you have clean, current data — even in markdown — migrating it into a structured system is trivial. The hard part was always getting people to enter the data in the first place.</p>

      <h2>The tech stack</h2>
      <ul>
        <li><strong>Obsidian</strong> — local-first markdown editor with relationship visualisation.</li>
        <li><strong>Dataview plugin</strong> — query engine for YAML frontmatter.</li>
        <li><strong>Templater plugin</strong> — dynamic page templates for consistency.</li>
        <li><strong>Any LLM agent</strong> — Claude Code reads <code>CLAUDE.md</code>, Cursor uses the same file. Model-agnostic.</li>
        <li><strong>Git</strong> — version control. Every change tracked. You can diff your pipeline over time.</li>
      </ul>

      <h2>Who this is for</h2>
      <ul>
        <li><strong>Solo consultants and founders</strong> managing 20–50 client relationships.</li>
        <li><strong>Small sales teams</strong> who tried Salesforce/HubSpot and abandoned it within 3 months.</li>
        <li><strong>Developers</strong> who want a CRM they can inspect, version-control, and extend.</li>
        <li><strong>Anyone</strong> whose &ldquo;CRM&rdquo; is actually a spreadsheet or scattered notes.</li>
      </ul>
      <p>If you have 50+ users and need RBAC, audit logs, and compliance reporting — you need <Link to="/opensource">a real CRM system</Link>. We build those too.</p>

      <h2>The bigger idea</h2>
      <p>The future of CRM isn&apos;t more dashboards, more fields, more automation rules. It&apos;s a system where the input is completely natural language, the structure emerges automatically, and the human never has to think about &ldquo;updating the CRM.&rdquo;</p>
      <p>The human&apos;s job: source leads, build relationships, close deals, think strategically. The LLM&apos;s job: everything else.</p>
      <blockquote>It&apos;s not AI on top of a CRM. It&apos;s a CRM that only exists because of AI.</blockquote>

      <div className="callout">
        <p><strong>Try it yourself.</strong> Free, open source, works with Claude, Cursor or any LLM agent. <a href="https://github.com/Aakeeo/llm-wiki-crm" target="_blank" rel="noopener noreferrer">Get it on GitHub</a>, or <Link to="/resources/llm-wiki-crm">read the full docs</Link>.</p>
      </div>

      <h2>FAQ</h2>

      <h3>Can this replace Salesforce?</h3>
      <p>No. If you have 50+ users, complex workflows, compliance requirements, and reporting needs — you need a production CRM. This is for the gap below that.</p>

      <h3>What LLM does it work with?</h3>
      <p>Any LLM agent that reads instruction files. Claude Code reads <code>CLAUDE.md</code>, Cursor uses the same file. Model-agnostic.</p>

      <h3>Is my data safe?</h3>
      <p>Your data never leaves your machine. Everything is local markdown files. No cloud sync, no third-party servers, no API calls to external services. Version control via Git gives you full history.</p>

      <h3>How many contacts can it handle?</h3>
      <p>Tested with 50+ contacts, 30+ companies, and 100+ interactions without issues. Practical limit depends on your LLM&apos;s context window.</p>

      <h3>Can I migrate to a real CRM later?</h3>
      <p>
        Yes. Because all data is structured YAML frontmatter, exporting to CSV or importing into <Link to="/services/suitecrm-implementation">SuiteCRM</Link>, HubSpot or any other system is straightforward.
      </p>

      <h3>Is this just &ldquo;Notion as a CRM&rdquo; again?</h3>
      <p>No. Notion-as-CRM still requires manual data entry into databases. This inverts the model: you write natural language, the LLM creates and maintains the structure. The input experience is fundamentally different.</p>
    </BlogPostLayout>
  );
}
